
import React, { useState, useEffect } from 'react';
import { useUser } from '@/context/UserContext';
import Layout from '@/components/Layout';
import { FadeIn } from '@/components/Animations';
import { Tabs, TabsContent, TabsList } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { generateCycleData, CycleData } from '@/utils/cycle-tracking';
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';

// Import our extracted components
import TabTriggers from '@/components/cycle-tracking/TabTriggers';
import DashboardTab from '@/components/cycle-tracking/tabs/DashboardTab';
import TestingTab from '@/components/dashboard/TestingTab';
import CycleInsightsTab from '@/components/cycle-tracking/tabs/CycleInsightsTab';
import AIConsultantTab from '@/components/cycle-tracking/tabs/AIConsultantTab';

const CycleTracking = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [cycleData, setCycleData] = useState<CycleData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Get the tab from URL query params
  const queryParams = new URLSearchParams(location.search);
  const tabFromUrl = queryParams.get('tab');
  const defaultTab = tabFromUrl || 'dashboard'; // Set dashboard as default tab instead of insights
  
  useEffect(() => {
    const fetchUserCycleData = async () => {
      if (!user?.id) return;
      
      try {
        setIsLoading(true);
        
        // Get health profile data
        const { data: healthProfile, error } = await supabase
          .from('health_profiles')
          .select('last_period_start, last_period_duration')
          .eq('user_id', user.id)
          .single();
        
        if (error) throw error;
        
        if (healthProfile?.last_period_start) {
          const lastPeriodStart = new Date(healthProfile.last_period_start);
          const periodLength = parseInt(healthProfile.last_period_duration) || 5;
          
          // Generate cycle data
          const calculatedCycleData = generateCycleData(lastPeriodStart, periodLength);
          setCycleData(calculatedCycleData);
        }
      } catch (error) {
        console.error('Error fetching cycle data:', error);
        toast.error('Failed to load cycle data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserCycleData();
  }, [user?.id]);
  
  const handleTabChange = (value: string) => {
    // Update URL when tab changes
    const newParams = new URLSearchParams(location.search);
    if (value === 'dashboard') {
      newParams.delete('tab');
    } else {
      newParams.set('tab', value);
    }
    
    navigate({
      pathname: location.pathname,
      search: newParams.toString()
    }, { replace: true });
  };
  
  return (
    <Layout>
      <div className="container max-w-6xl py-4 md:py-8">
        <FadeIn>
          <div className="flex items-center mb-4 md:mb-6">
            <Button 
              variant="ghost" 
              size="sm" 
              className="mr-2" 
              onClick={() => navigate('/integrated-dashboard')}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
            <h1 className="text-xl md:text-3xl font-display font-medium">
              Hormone Cycle Tracking
            </h1>
          </div>
          
          <div className="mb-4 md:mb-6">
            <p className="text-muted-foreground text-sm md:text-base">
              Understand how your hormones fluctuate throughout your menstrual cycle and gain insights to optimize your health and wellbeing.
            </p>
          </div>
          
          <Tabs defaultValue={defaultTab} value={defaultTab} onValueChange={handleTabChange}>
            <TabsList className="mb-4 md:mb-6 w-full overflow-x-auto no-scrollbar px-0.5 py-1">
              <TabTriggers isMobile={isMobile} />
            </TabsList>
            
            <TabsContent value="dashboard">
              <DashboardTab cycleData={cycleData} isLoading={isLoading} />
            </TabsContent>
            
            <TabsContent value="insights">
              <CycleInsightsTab cycleData={cycleData} isLoading={isLoading} />
            </TabsContent>
            
            <TabsContent value="consultant">
              <AIConsultantTab cycleData={cycleData} isLoading={isLoading} />
            </TabsContent>
            
            <TabsContent value="testing">
              <TestingTab />
            </TabsContent>
          </Tabs>
        </FadeIn>
      </div>
    </Layout>
  );
};

export default CycleTracking;
